import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { TimePicker } from '@/components/ui/time-picker';
import { useHwcgFlag } from 'utils/featureFlags';

import {
  MAX_SCHEDULED_OPERATIONAL_PERIODS,
  type OperationalSchedule,
  type OperationalSchedulePeriod,
} from './operationalSchedule';

/** Above the full-screen create-incident dialog, same raise as its other menus. */
const PICKER_LAYER = 'tw:z-[60]';

function reindex(
  periods: OperationalSchedulePeriod[]
): OperationalSchedulePeriod[] {
  return periods.map((period, index) => ({ ...period, index: index + 1 }));
}

function localParts(iso: string): {
  day: Date | undefined;
  hours: string;
  minutes: string;
} {
  if (!iso.trim()) return { day: undefined, hours: '08', minutes: '00' };
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return { day: undefined, hours: '08', minutes: '00' };
  }
  return {
    day: new Date(date.getFullYear(), date.getMonth(), date.getDate()),
    hours: String(date.getHours()).padStart(2, '0'),
    minutes: String(date.getMinutes()).padStart(2, '0'),
  };
}

function isoFromParts(day: Date, hours: string, minutes: string): string {
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    Number(hours),
    Number(minutes),
    0,
    0
  ).toISOString();
}

/** IANA zone plus the short name and offset the browser is using right now. */
function browserTimeZoneLabel(now: Date): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const part = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(undefined, options)
      .formatToParts(now)
      .find((item) => item.type === 'timeZoneName')?.value;
  const extras = [
    part({ timeZoneName: 'short' }),
    part({ timeZoneName: 'shortOffset' }),
  ].filter(
    (item, index, all): item is string =>
      Boolean(item) && item !== zone && all.indexOf(item) === index
  );
  if (!zone) return extras.join(', ') || 'your local timezone';
  return extras.length > 0 ? `${zone} (${extras.join(', ')})` : zone;
}

function PeriodInstantFields({
  id,
  label,
  value,
  disabled,
  datePlaceholder,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  disabled?: boolean;
  datePlaceholder: string;
  onChange: (iso: string) => void;
}) {
  const parts = localParts(value);
  const [pendingTime, setPendingTime] = useState('');
  const time = value.trim() ? `${parts.hours}:${parts.minutes}` : pendingTime;

  return (
    <div className="tw:grid tw:gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <DatePicker
          id={id}
          value={parts.day}
          disabled={disabled}
          placeholder={datePlaceholder}
          aria-label={`${label} date`}
          positionerClassName={PICKER_LAYER}
          onChange={(next) => {
            if (!next) {
              onChange('');
              return;
            }
            const [hours = '00', minutes = '00'] = (time || '00:00').split(':');
            onChange(isoFromParts(next, hours, minutes));
            setPendingTime('');
          }}
        />
        <TimePicker
          id={`${id}-time`}
          value={time}
          disabled={disabled}
          aria-label={`${label} time`}
          positionerClassName={PICKER_LAYER}
          onChange={(next) => {
            if (!parts.day) {
              setPendingTime(next);
              return;
            }
            const [hours = '00', minutes = '00'] = next.split(':');
            onChange(isoFromParts(parts.day, hours, minutes));
          }}
        />
      </div>
    </div>
  );
}

/**
 * One to four operational period windows. Period 1 can start when the incident
 * is created instead of at a chosen clock time. Times are the browser's local
 * timezone.
 */
export function OperationalScheduleFields({
  schedule,
  onChange,
}: {
  schedule: OperationalSchedule;
  onChange: (next: OperationalSchedule) => void;
}) {
  const hwcg = useHwcgFlag();
  const [timeZoneLabel, setTimeZoneLabel] = useState(
    () =>
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'your local timezone'
  );

  useEffect(() => {
    setTimeZoneLabel(browserTimeZoneLabel(new Date()));
  }, []);

  if (!hwcg) return null;

  const periods = schedule.periods.length
    ? schedule.periods
    : [{ index: 1, start: '', end: '' }];

  const updatePeriod = (
    index: number,
    patch: Partial<Pick<OperationalSchedulePeriod, 'start' | 'end'>>
  ) => {
    onChange({
      ...schedule,
      periods: periods.map((period) =>
        period.index === index ? { ...period, ...patch } : period
      ),
    });
  };

  return (
    <section className="tw:space-y-3">
      <div>
        <h3 className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
          Operational periods
        </h3>
        <p className="tw:mt-1 tw:text-xs tw:text-muted-foreground">
          Schedule up to four windows. Each one starts when the previous one
          stops. Times use your browser timezone: {timeZoneLabel}.
        </p>
      </div>
      <div className="tw:flex tw:items-center tw:gap-2">
        <Checkbox
          id="hwcg-start-first-on-create"
          checked={schedule.startFirstOnCreate}
          onCheckedChange={(checked) => {
            const startFirstOnCreate = checked === true;
            onChange({
              ...schedule,
              startFirstOnCreate,
              periods: periods.map((period) =>
                period.index === 1 && startFirstOnCreate
                  ? { ...period, start: '' }
                  : period
              ),
            });
          }}
        />
        <Label htmlFor="hwcg-start-first-on-create" className="tw:font-normal">
          Start operational period 1 on incident creation
        </Label>
      </div>
      <div className="tw:flex tw:items-start tw:gap-2">
        <Checkbox
          id="hwcg-auto-advance"
          checked={schedule.autoAdvance ?? true}
          onCheckedChange={(checked) =>
            onChange({ ...schedule, autoAdvance: checked === true })
          }
        />
        <div className="tw:grid tw:gap-0.5">
          <Label htmlFor="hwcg-auto-advance" className="tw:font-normal">
            Automatically advance at each scheduled start
          </Label>
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            Stops after the last scheduled period.
          </p>
        </div>
      </div>
      <div className="tw:space-y-3">
        {periods.map((period) => {
          const startsOnCreate =
            period.index === 1 && schedule.startFirstOnCreate;
          return (
            <div
              key={period.index}
              className="tw:grid tw:gap-3 tw:rounded-md tw:border tw:p-3 tw:sm:grid-cols-2"
            >
              <div className="tw:sm:col-span-2 tw:flex tw:items-center tw:justify-between tw:gap-2">
                <div className="tw:text-sm tw:font-medium">
                  Operational period {period.index}
                </div>
                {periods.length > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      onChange({
                        ...schedule,
                        periods: reindex(
                          periods.filter((item) => item.index !== period.index)
                        ),
                      })
                    }
                  >
                    Remove
                  </Button>
                ) : null}
              </div>
              <PeriodInstantFields
                id={`hwcg-op-${period.index}-start`}
                label="Start"
                value={startsOnCreate ? '' : period.start}
                disabled={startsOnCreate}
                datePlaceholder={startsOnCreate ? 'On creation' : 'Select date'}
                onChange={(start) => updatePeriod(period.index, { start })}
              />
              <PeriodInstantFields
                id={`hwcg-op-${period.index}-end`}
                label="Stop"
                value={period.end}
                datePlaceholder="Select date"
                onChange={(end) => updatePeriod(period.index, { end })}
              />
            </div>
          );
        })}
      </div>
      {periods.length < MAX_SCHEDULED_OPERATIONAL_PERIODS ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            onChange({
              ...schedule,
              periods: reindex([
                ...periods,
                { index: periods.length + 1, start: '', end: '' },
              ]),
            })
          }
        >
          Add operational period
        </Button>
      ) : null}
    </section>
  );
}
